import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useBack } from '../hooks';
import { C, cs } from '../styles/common';
import { RecordCheck } from '../components/ScheduleHistoryModal';

// History (Phase 6, 1 Oct 2026).
//
// One place for "what happened": every change made in the app -- on the board
// and to every record -- and everything no longer in use, which is read from
// the same log rather than kept as a separate list (the director's idea). It
// replaced the Archive. Any page can link straight to one thing's history:
// /admin/history?type=part&id=61.

const TEAL = C.teal, AMBER = '#f59e0b', GREEN = '#34d399', RED = '#ef4444';
const DIM = '#9ca3af', SUNK = '#171b21';

// Every kind of thing History knows, in the order the filters show them.
// `open` is where the thing itself lives, for the "Open" link.
const TYPES = {
  board:     { label: 'Board',              icon: 'fa-calendar-days' },
  part:      { label: 'Parts',              one: 'Part',              icon: 'fa-cube',          open: id => `/admin/parts/${id}` },
  product:   { label: 'Products',           one: 'Product',           icon: 'fa-box',           open: () => '/admin/parts' },
  mould:     { label: 'Moulds',             one: 'Mould',             icon: 'fa-shapes',        open: () => '/admin/moulds' },
  machine:   { label: 'Machines',           one: 'Machine',           icon: 'fa-industry',      open: () => '/admin/machines' },
  recipe:    { label: 'Recipes',            one: 'Recipe',            icon: 'fa-flask',         open: () => '/admin/materials' },
  material:  { label: 'Materials',          one: 'Material',          icon: 'fa-droplet',       open: () => '/admin/materials' },
  order:     { label: 'Orders',             one: 'Order',             icon: 'fa-boxes-stacked', open: () => '/admin/orders' },
  job:       { label: 'Production records', one: 'Production record', icon: 'fa-wrench',        open: () => '/admin/archive/jobs' },
  process:   { label: 'Process',            one: 'Process',           icon: 'fa-sliders',       open: id => `/process/optimise/${id}` },
  checklist: { label: 'Checklists',         one: 'Checklist',         icon: 'fa-list-check' },
  closure:   { label: 'Closures',           one: 'Closure',           icon: 'fa-calendar-xmark', open: () => '/admin/closures' },
  user:      { label: 'People',             one: 'Person',            icon: 'fa-user',          open: id => `/admin/users/${id}` },
  setting:   { label: 'Settings',           one: 'Setting',           icon: 'fa-gear' },
  customer:  { label: 'Customers',          one: 'Customer',          icon: 'fa-address-book',  open: id => `/admin/customers/${id}` },
  customer_item: { label: 'Customer items', one: 'Customer item',     icon: 'fa-tag',           open: () => '/admin/customers' },
  invoice:   { label: 'Invoices',           one: 'Invoice',           icon: 'fa-file-invoice',  open: () => '/admin/invoices' },
  price:     { label: 'Prices',             one: 'Price',             icon: 'fa-tag',           open: () => '/admin/customers' },
  container: { label: 'Packaging',        one: 'Packaging item',    icon: 'fa-box-open',      open: () => '/admin/packaging' },
  compliance: { label: 'Compliance',      one: 'Compliance',        icon: 'fa-recycle',       open: () => '/admin/packaging' },
  // Findings dismissed from the at-risk list, and brought back (19 Oct 2026, 078).
  risk:      { label: 'At-risk list',       one: 'At-risk finding',   icon: 'fa-triangle-exclamation', open: () => '/admin/schedule' },
};
// Where something out of use is brought back from, where that is not its own page.
const RETIRED_HOME = {
  part: () => '/admin/parts?discontinued=show',
  machine: () => '/admin/archive/equipment',
  mould: () => '/admin/archive/equipment',
  order: () => '/admin/orders',   // completed orders sit under the active ones (2 Oct 2026)
  job: () => '/admin/archive/jobs',
};

// How each kind of entry reads. Reality is green: what the floor did happened;
// everything else is a decision about the future or about a record.
const KIND = {
  completed:  { icon: 'fa-flag-checkered', colour: GREEN, label: 'Finished' },
  started:    { icon: 'fa-play',           colour: GREEN, label: 'Started' },
  weight:     { icon: 'fa-weight-scale',   colour: GREEN, label: 'Weighed' },
  inspection: { icon: 'fa-magnifying-glass', colour: GREEN, label: 'Inspected' },
  checklist:  { icon: 'fa-list-check',     colour: GREEN, label: 'Checklist' },
  scrap:      { icon: 'fa-recycle',        colour: GREEN, label: 'Scrap' },
  scheduled:  { icon: 'fa-calendar-plus',  colour: TEAL,  label: 'Scheduled' },
  reflow:     { icon: 'fa-arrows-left-right-to-line', colour: TEAL, label: 'Re-flowed' },
  moved:      { icon: 'fa-right-left',     colour: TEAL,  label: 'Moved' },
  resized:    { icon: 'fa-arrows-left-right', colour: TEAL, label: 'Resized' },
  replanned:  { icon: 'fa-pen-to-square',  colour: TEAL,  label: 'Re-planned' },
  split:      { icon: 'fa-scissors',       colour: TEAL,  label: 'Split' },
  duplicated: { icon: 'fa-copy',           colour: TEAL,  label: 'Copied' },
  quantity:   { icon: 'fa-hashtag',        colour: TEAL,  label: 'Quantity' },
  pushed:     { icon: 'fa-arrow-right-long', colour: C.muted, label: 'Pushed' },
  window:     { icon: 'fa-layer-group',    colour: AMBER, label: 'Shift window' },
  hold:       { icon: 'fa-lock',           colour: AMBER, label: 'Hold' },
  removed:    { icon: 'fa-trash',          colour: RED,   label: 'Removed' },
  put_back:   { icon: 'fa-clock-rotate-left', colour: AMBER, label: 'Put back' },
  undone:     { icon: 'fa-rotate-left',    colour: C.muted, label: 'Undone' },
  redone:     { icon: 'fa-rotate-right',   colour: C.muted, label: 'Redone' },
  job_deleted:{ icon: 'fa-trash',          colour: RED,   label: 'Deleted' },
  // record entries
  created:    { icon: 'fa-plus',           colour: TEAL,  label: 'Created' },
  updated:    { icon: 'fa-pen',            colour: TEAL,  label: 'Changed' },
  job_edited: { icon: 'fa-pen',            colour: TEAL,  label: 'Changed' },
  deleted:    { icon: 'fa-trash',          colour: RED,   label: 'Deleted' },
  reverted:   { icon: 'fa-clock-rotate-left', colour: AMBER, label: 'Reverted' },
  retired:    { icon: 'fa-box-archive',    colour: AMBER, label: 'Out of use' },
  restored:   { icon: 'fa-rotate-left',    colour: GREEN, label: 'Back in use' },
  archived:   { icon: 'fa-box-archive',    colour: AMBER, label: 'Archived' },
  acknowledged: { icon: 'fa-check',        colour: GREEN, label: 'Seen' },
};
const kindOf = k => KIND[k] || { icon: 'fa-circle', colour: C.muted, label: k };

const ORIGIN = { calculated: 'worked out by the app', measured: 'measured' };

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short',
    year: that.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}
const shortDate = iso => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
const timeOf = iso => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const longDate = d => new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export default function History() {
  const navigate = useNavigate();
  const goBack = useBack('/admin');
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'retired' ? 'retired' : 'changes';
  const type = params.get('type') || '';
  const id = params.get('id') || '';
  const [retiredCount, setRetiredCount] = useState(null);

  // Every filter lives in the address, so a link from any page lands right and
  // Back walks back through what you looked at.
  const go = useCallback((next) => {
    const p = {};
    const merged = { view, type, id, q: params.get('q') || '', ...next };
    for (const [k, v] of Object.entries(merged)) if (v && !(k === 'view' && v === 'changes')) p[k] = v;
    setParams(p);
  }, [params, setParams, view, type, id]);

  useEffect(() => {
    api.get('/api/v1/history/retired')
      .then(r => setRetiredCount(r.data.items.length))
      .catch(() => setRetiredCount(null));
  }, []);

  return (
    <div style={{ ...cs.page, padding: 'clamp(16px, 3vw, 24px)', paddingBottom: 84, boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 12 }}>
          <button onClick={goBack} style={st.crumbBtn}>
            <i className="fa-solid fa-chevron-left" style={{ fontSize: 10 }} /> Back
          </button>
          <span style={{ color: C.mutedDark }}>·</span>
          <span style={{ color: DIM }}>History</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ color: TEAL, fontSize: 22 }} /> History
            </h1>
            <p style={{ color: C.muted, fontSize: 14, margin: '6px 0 0' }}>
              Every change made in the app, and everything no longer in use.
            </p>
          </div>
          <RecordCheck />
        </div>

        <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.border}`, marginBottom: 18 }}>
          {[['changes', 'What changed'], ['retired', 'No longer in use' + (retiredCount != null ? ` (${retiredCount})` : '')]].map(([k, label]) => (
            <button key={k} onClick={() => go({ view: k, type: k === view ? type : '', id: k === view ? id : '', q: '' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px', fontSize: 14,
                       fontWeight: view === k ? 700 : 600, color: view === k ? TEAL : C.muted,
                       borderBottom: `2px solid ${view === k ? TEAL : 'transparent'}`, marginBottom: -1 }}>
              {label}
            </button>
          ))}
        </div>

        {view === 'retired'
          ? <NoLongerInUse onFocus={(t, i) => go({ view: 'changes', type: t, id: i, q: '' })} navigate={navigate} />
          : id && TYPES[type]
            ? <ScopeView type={type} id={id} go={go} navigate={navigate} />
            : <WhatChanged type={type} id={id} q={params.get('q') || ''} go={go} navigate={navigate} />}
      </div>
    </div>
  );
}

// ── What changed ────────────────────────────────────────────────────────────

function WhatChanged({ type, id, q, go, navigate }) {
  const [entries, setEntries] = useState([]);
  const [names, setNames]     = useState({});
  const [focus, setFocus]     = useState(null);
  const [next, setNext]       = useState(null);
  const [reach, setReach]     = useState(undefined);   // board entries from this seq on can show the board then
  const [loading, setLoading] = useState(true);
  const [more, setMore]       = useState(false);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState(q);
  const [reloadKey, setReloadKey] = useState(0);
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    api.get('/api/v1/reasons', { params: { all: true } })
      .then(r => setReasons(r.data.reasons || []))
      .catch(() => setReasons([]));
  }, []);

  useEffect(() => { setSearch(q); }, [q]);
  // Typing searches after a short pause, not on every key.
  useEffect(() => {
    if (search === q) return undefined;
    const t = setTimeout(() => go({ q: search }), 350);
    return () => clearTimeout(t);
  }, [search, q, go]);

  const query = useMemo(() => ({ ...(type ? { type } : {}), ...(id ? { id } : {}), ...(q ? { q } : {}), limit: 50 }), [type, id, q]);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    api.get('/api/v1/history/timeline', { params: query })
      .then(({ data }) => {
        if (!live) return;
        setEntries(data.entries); setNames(data.names || {}); setFocus(data.focus); setNext(data.next);
        setReach(data.board_reach_seq);
      })
      .catch(e => live && setError(e.response?.data?.error || e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [query, reloadKey]);

  async function loadMore() {
    setMore(true);
    try {
      const { data } = await api.get('/api/v1/history/timeline', { params: { ...query, before: next } });
      setEntries(es => [...es, ...data.entries]);
      setNames(n => mergeNames(n, data.names || {}));
      setNext(data.next);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setMore(false);
    }
  }

  const days = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const k = dayLabel(e.at);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    return [...m.entries()];
  }, [entries]);

  const focusType = TYPES[type];

  return (
    <>
      {id && focusType ? (
        <FocusHeader type={type} id={id} focus={focus} onClear={() => go({ type: '', id: '' })}
          onKind={() => go({ id: '' })} navigate={navigate} />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <Chip active={!type} onClick={() => go({ type: '', id: '' })}>Everything</Chip>
          {Object.entries(TYPES).map(([k, t]) => (
            <Chip key={k} active={type === k} onClick={() => go({ type: k, id: '' })}>
              <i className={`fa-solid ${t.icon}`} style={{ fontSize: 11, marginRight: 6 }} />{t.label}
            </Chip>
          ))}
        </div>
      )}

      <div style={{ position: 'relative', marginBottom: 18 }}>
        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.muted, fontSize: 12 }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={id ? 'Search this history…' : 'Search — a part, a machine, an order, a word…'}
          style={{ ...cs.input, paddingLeft: 34, background: SUNK }} />
      </div>

      {error && <Notice tone={AMBER}>{error}</Notice>}

      {loading ? (
        <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
      ) : entries.length === 0 ? (
        <Empty title={q ? 'Nothing matches that' : 'Nothing recorded yet'}
          body={q ? 'Try a shorter word, or clear the search.'
            : id ? 'Nothing has changed here since History started recording it. Board changes are recorded from 24 Sep 2026, everything else from 30 Sep 2026.'
            : 'Changes appear here as they are made.'} />
      ) : (
        <>
          {days.map(([day, list]) => (
            <div key={day} style={{ marginBottom: 22 }}>
              <div style={st.eyebrow}>{day}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {list.map(e => (
                  <Entry key={e.key} e={e} names={names} focused={!!id} focusType={type} focusId={id}
                    onChanged={() => setReloadKey(k => k + 1)} reasons={reasons}
                    boardReach={reach} onSeeBoard={(ev) => navigate(`/admin/history/board?event=${ev.id}`)}
                    onFocus={(t, i) => go({ type: t, id: String(i), q: '' })} />
                ))}
              </div>
            </div>
          ))}
          {next && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button onClick={loadMore} disabled={more} style={{ ...st.ghost, opacity: more ? 0.6 : 1 }}>
                {more ? 'Loading…' : 'Show earlier changes'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function mergeNames(a, b) {
  const out = { ...a };
  for (const k of Object.keys(b)) out[k] = { ...(a[k] || {}), ...b[k] };
  return out;
}

function FocusHeader({ type, id, focus, onClear, onKind, navigate, count }) {
  const t = TYPES[type];
  const retired = focus && focus.retired;
  const open = retired && RETIRED_HOME[type] ? RETIRED_HOME[type](id) : t.open && t.open(id);
  return (
    <div style={{ ...cs.card, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
      <div style={{ width: 38, height: 38, borderRadius: 9, background: C.tealDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={`fa-solid ${t.icon}`} style={{ color: TEAL, fontSize: 16 }} />
      </div>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
          History of {/^[aeiou]/i.test(t.one) ? 'an' : 'a'} {t.one.toLowerCase()}{count != null ? ` · ${count} ${count === 1 ? 'entry' : 'entries'}` : ''}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {(focus && focus.name) || `${t.one} ${id}`}
          {retired && (
            <span style={{ fontSize: 11, fontWeight: 700, color: AMBER, background: `${AMBER}18`, border: `1px solid ${AMBER}44`, borderRadius: 5, padding: '2px 7px' }}>
              No longer in use{retired.date_known ? ` since ${longDate(retired.retired_on)}` : ''}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {open && <button onClick={() => navigate(open)} style={st.ghost}>Open <i className="fa-solid fa-arrow-right" style={{ fontSize: 10, marginLeft: 4 }} /></button>}
        <button onClick={onKind} style={st.ghost}>All {t.label.toLowerCase()}</button>
        <button onClick={onClear} style={st.ghost}>Everything</button>
      </div>
    </div>
  );
}

function Entry({ e, names, focused, focusType, focusId, onFocus, onChanged, reasons = [], withDate = false, boardReach, onSeeBoard }) {
  // The board can be shown as it was just after this entry (6 Oct 2026) --
  // for every board entry since the log began keeping its pictures.
  const canSeeBoard = e.source === 'board' && onSeeBoard && boardReach !== undefined
    && (boardReach === null || e.seq >= boardReach);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(e.reason);
  const [editingReason, setEditingReason] = useState(false);
  const reasonLabel = reason && reason.code ? ((reasons.find(r => r.code === reason.code) || {}).label || reason.code) : null;
  const [reverting, setReverting] = useState(false);
  const canRevert = e.source === 'record' && REVERTIBLE.has(e.type) && e.kind !== 'deleted';
  const k = kindOf(e.kind);
  const t = e.type ? TYPES[e.type] : TYPES.board;
  const diff = e.source === 'record' && e.kind !== 'created' && e.kind !== 'deleted'
    ? (e.changed_fields || []).filter(f => !/^(updated_at|created_at)$/.test(f)) : [];
  const links = e.source === 'board'
    ? ['part', 'machine', 'mould', 'order'].map(kind => {
        const lid = e.links && e.links[kind];
        const n = lid != null && names[kind] && names[kind][lid];
        // The thing this page is about needs no link to itself.
        if (focused && kind === focusType && String(lid) === String(focusId)) return null;
        return n ? { kind, id: lid, name: n } : null;
      }).filter(Boolean)
    : [];

  return (
    <div style={{ display: 'flex', gap: 11, padding: '10px 13px', background: SUNK, border: `1px solid ${C.border}`,
                  borderRadius: 9, alignItems: 'flex-start', opacity: e.undone ? 0.55 : 1 }}>
      <div title={k.label} style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0, marginTop: 1,
                    background: `${k.colour}1c`, border: `1px solid ${k.colour}3a`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`fa-solid ${k.icon}`} style={{ color: k.colour, fontSize: 11 }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {e.source === 'record' && !focused && e.name && (
          <button onClick={() => onFocus(e.type, e.record_id)} style={st.nameLink} title={`This ${t.one.toLowerCase()}'s history`}>
            <i className={`fa-solid ${t.icon}`} style={{ fontSize: 10, marginRight: 5, color: C.muted }} />{e.name}
          </button>
        )}
        <div style={{ fontSize: 13, color: '#e5e7eb', lineHeight: 1.5, overflowWrap: 'anywhere' }}>{e.summary}</div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{withDate ? `${shortDate(e.at)} · ` : ''}{timeOf(e.at)}</span>
          <span>{k.label}{e.source === 'board' ? ' · board' : ''}</span>
          {e.who && <span>{e.who}</span>}
          {ORIGIN[e.origin] && <span>{ORIGIN[e.origin]}</span>}
          {e.version && <span>version {e.version}</span>}
          {e.undone && <span style={{ color: AMBER }}>undone</span>}
        </div>

        {links.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
            {links.map(l => (
              <button key={l.kind} onClick={() => onFocus(l.kind, l.id)} style={st.linkChip} title={`${TYPES[l.kind].one}'s history`}>
                <i className={`fa-solid ${TYPES[l.kind].icon}`} style={{ fontSize: 9, marginRight: 5 }} />{l.name}
              </button>
            ))}
          </div>
        )}

        {diff.length > 0 && e.before && e.after && (
          <div style={{ marginTop: 7 }}>
            <button onClick={() => setOpen(o => !o)} style={st.textBtn}>
              <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 9, marginRight: 5 }} />
              {open ? 'Hide' : 'Show'} what changed ({diff.length})
            </button>
            {open && <ChangeTable fields={diff} before={e.before} after={e.after} />}
          </div>
        )}

        {reason && (reasonLabel || reason.note) && !editingReason && (
          <div style={{ fontSize: 12.5, color: DIM, marginTop: 6, lineHeight: 1.5 }}>
            <i className="fa-solid fa-comment" style={{ fontSize: 10, marginRight: 6, color: C.muted }} />
            {reasonLabel && <span style={{ fontWeight: 600 }}>{reasonLabel}</span>}
            {reasonLabel && reason.note ? ' — ' : ''}
            {reason.note}
          </div>
        )}
        {editingReason && (
          <ReasonEditor e={e} reason={reason} reasons={reasons}
            onClose={() => setEditingReason(false)}
            onSaved={r => { setReason(r); setEditingReason(false); }} />
        )}

        {!editingReason && !reverting && (
          <div style={{ display: 'flex', gap: 16, marginTop: 7, flexWrap: 'wrap' }}>
            <button onClick={() => setEditingReason(true)} style={st.textBtn}>
              <i className="fa-solid fa-comment" style={{ fontSize: 10, marginRight: 5 }} />
              {reason && (reason.code || reason.note) ? 'Change the reason' : 'Add a reason'}
            </button>
            {canRevert && (
              <button onClick={() => setReverting(true)} style={{ ...st.textBtn, color: AMBER }}>
                <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 10, marginRight: 5 }} />Revert to this
              </button>
            )}
            {canSeeBoard && (
              <button onClick={() => onSeeBoard(e)} style={st.textBtn} title="The whole board as it was just after this — look only">
                <i className="fa-solid fa-table-cells" style={{ fontSize: 10, marginRight: 5 }} />See the board at this point
              </button>
            )}
          </div>
        )}
        {reverting && (
          <RevertPanel e={e} onClose={() => setReverting(false)}
            onDone={() => { setReverting(false); if (onChanged) onChanged(); }} />
        )}
      </div>
    </div>
  );
}

// Fields whose stored names read badly (7 Oct 2026: a mould's shot limit,
// refurbishment and service) -- the same words as the history's summaries.
const FIELD_WORDS = {
  shot_life: 'shot limit', shot_limit_kind: 'shot limit is', shots: 'shot count', shots_counted_on: 'shot count as of',
  refurb_int: 'refurbish every (shots)', service_every_shots: 'service every (shots)',
  service_every_n: 'service every', service_every_unit: 'service every (weeks or months)',
  maintenance_log: 'log',
};
const VALUE_WORDS = {
  shot_limit_kind: { warranty: 'a warranty', shot_life: 'a shot life' },
  packing_count_method: { hand: 'by hand', weighed: 'by weight', machine_counter: 'by the machine counter' },
  packing_place: { machine: 'at the machine', packing_table: 'at the packing table' },
};

// The fields that moved: from, to. A table, because a sentence can drift from
// the data and a table cannot.
function ChangeTable({ fields, before, after, heads = ['Field', 'Was', 'Now'] }) {
  const show = (v, f) => {
    if (v === null || v === undefined || v === '') return <span style={{ color: C.mutedDark }}>empty</span>;
    if (f && VALUE_WORDS[f] && VALUE_WORDS[f][v]) return VALUE_WORDS[f][v];
    // Packaging: each layer's quantity, innermost first, as on the product form.
    if (f === 'packaging_layers' && Array.isArray(v)) {
      return v.length ? v.map(l => (l && l.qty != null ? l.qty : '?')).join(' / ') : <span style={{ color: C.mutedDark }}>empty</span>;
    }
    if (typeof v === 'object') {
      // A record kept inside the entry -- the part a line is for -- reads as
      // its number, then its name in bold (the director, 6 Oct 2026), not code.
      if (!Array.isArray(v) && (v.name || v.part_number)) {
        return (
          <span>
            {v.part_number && <span style={{ marginRight: 6 }}>{v.part_number}</span>}
            {v.name && <b>{v.name}</b>}
          </span>
        );
      }
      const s = JSON.stringify(v);
      return <span title={s} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{s.length > 90 ? s.slice(0, 90) + '…' : s}</span>;
    }
    return String(v);
  };
  return (
    <div style={{ marginTop: 7, border: `1px solid ${C.border}`, borderRadius: 7, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: C.surfaceAlt, color: C.muted, textAlign: 'left' }}>
            {heads.map(h => <th key={h} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {fields.map(f => (
            <tr key={f} style={{ borderTop: `1px solid ${C.border}` }}>
              <td style={{ padding: '6px 10px', color: DIM, whiteSpace: 'nowrap' }}>{FIELD_WORDS[f] || f.replace(/_/g, ' ')}</td>
              <td style={{ padding: '6px 10px', color: DIM, fontVariantNumeric: 'tabular-nums' }}>{show(before[f], f)}</td>
              <td style={{ padding: '6px 10px', color: '#e5e7eb', fontVariantNumeric: 'tabular-nums' }}>{show(after[f], f)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── One thing's whole history ───────────────────────────────────────────────
//
// The prototype's scoped view (24 Sep), made real (1 Oct 2026): the table its
// own question needs, what it touches, and its entries oldest first -- because
// a history read to understand how something ended up this way only makes
// sense forwards -- in three streams: what it is, where it was on the board,
// and what really ran.

const STREAMS = {
  spec:       { colour: TEAL,  title: 'Specification',   says: 'What it is and how it is made.' },
  plan:       { colour: AMBER, title: 'On the board',    says: 'Where it was scheduled, and every time that moved.' },
  production: { colour: GREEN, title: 'What really ran', says: 'Reported by the floor, or closed from the board. Records of what happened: never edited or undone.' },
};
const STREAM_WORDS = {
  part:    { spec: ['Specification', 'What it is and how it is made: the part, its mould, its recipes and their materials.'] },
  mould:   { spec: ['The tool itself', 'Cavities, the machines it fits, its service life, and every time one of those changed.'],
             plan: ['On the board', 'Every run this tool was scheduled for, and every time that moved.'] },
  machine: { spec: ['The machine itself', 'Capacity, status, and every time one of those changed.'],
             plan: ['On the board', 'Work scheduled onto it, windows declared, and every move.'] },
  recipe:  { spec: ['Versions', 'Every version of this recipe and what changed between them.'],
             plan: ['Where it was used', 'A recipe is never scheduled. These are the runs that named it.'] },
  material:{ spec: ['Properties', 'What every plan using this material is worked out from.'],
             plan: ['Where it was used', 'A material is never scheduled. These are the runs that reached it.'] },
  user:    { spec: ['Who they are', 'Their details, role and access.'],
             plan: ['Decisions they made', 'Changes to the board, on their say-so.'],
             production: ['What they did on the floor', 'Runs started and finished, weights and checks. Facts, never edited.'] },
};
const streamWords = (type, k) => {
  const w = (STREAM_WORDS[type] || {})[k];
  return w ? { ...STREAMS[k], title: w[0], says: w[1] } : STREAMS[k];
};
// An order is short-lived with one story, so it reads as one; everything else
// has lives that move at different speeds, so they are kept apart.
const TOGETHER_BY_DEFAULT = new Set(['order']);

const TABLE = {
  part:    { title: 'Orders this part has served', cols: ['order', 'qty', 'machine', 'planned', 'ran', 'made', 'outcome'] },
  machine: { title: 'What has run on it',          cols: ['part', 'order', 'qty', 'planned', 'ran', 'made', 'outcome'] },
  mould:   { title: 'Runs on this tool',           cols: ['part', 'machine', 'order', 'planned', 'ran', 'made', 'outcome'] },
  order:   { title: 'Its runs',                    cols: ['part', 'machine', 'qty', 'planned', 'ran', 'made', 'outcome'] },
};
const COL_HEAD = { order: 'Order', qty: 'Quantity', machine: 'Machine', part: 'Part', planned: 'Planned', ran: 'Actually ran', made: 'Made', outcome: 'Outcome' };
const STAMP = {
  ok: { colour: GREEN }, late: { colour: RED }, running: { colour: TEAL },
  planned: { colour: DIM }, unknown: { colour: C.muted }, none: { colour: C.muted },
};
const span = (a, b) => {
  if (!a) return null;
  const d = x => new Date(x + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return a === b || !b ? d(a) : `${d(a)}–${d(b)}`;
};

function ScopeView({ type, id, go, navigate }) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [split, setSplit]     = useState(!TOGETHER_BY_DEFAULT.has(type));
  const [all, setAll]         = useState({});

  useEffect(() => { setSplit(!TOGETHER_BY_DEFAULT.has(type)); setAll({}); }, [type, id]);
  useEffect(() => {
    api.get('/api/v1/reasons', { params: { all: true } }).then(r => setReasons(r.data.reasons || [])).catch(() => {});
  }, []);
  useEffect(() => {
    let live = true;
    setError(null);
    api.get('/api/v1/history/scope', { params: { type, id } })
      .then(r => live && setData(r.data))
      .catch(e => live && setError(e.response?.data?.error || e.message));
    return () => { live = false; };
  }, [type, id, reloadKey]);

  if (error) return <Notice tone={AMBER}>{error}</Notice>;
  if (!data) return <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>;

  const focus = (t, i) => go({ type: t, id: String(i), q: '' });
  const oldest = data.entries.slice().reverse();
  const entryProps = { names: data.names, focused: true, focusType: type, focusId: id, onFocus: focus,
                       onChanged: () => setReloadKey(k => k + 1), reasons, withDate: true,
                       boardReach: data.board_reach_seq, onSeeBoard: (ev) => navigate(`/admin/history/board?event=${ev.id}`) };

  return (
    <>
      <FocusHeader type={type} id={id} focus={data.focus} onClear={() => go({ type: '', id: '' })}
        onKind={() => go({ id: '' })} navigate={navigate} count={data.entries.length} />

      {type === 'order' && data.verdict && <OrderVerdict v={data.verdict} />}
      {type === 'order' && data.promise && data.promise.rows.length > 0 && <PromiseOverTime p={data.promise} />}
      {type === 'mould' && data.wear && <MouldWear w={data.wear} mouldId={id} onChanged={() => setReloadKey(k => k + 1)} />}
      {TABLE[type] && data.runs.length > 0 && <RunsTable type={type} runs={data.runs} names={data.names} onFocus={focus} />}
      <Touches type={type} id={id} entries={data.entries} runs={data.runs} names={data.names} onFocus={focus} />

      {oldest.length === 0 ? (
        <Empty title="Nothing recorded yet"
          body="Board changes are recorded from 24 Sep 2026, everything else from 30 Sep 2026. Changes appear here as they are made." />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '22px 0 12px' }}>
            <div style={{ ...st.eyebrow, marginBottom: 0 }}>What changed, oldest first · {oldest.length}</div>
            <div style={{ marginLeft: 'auto', display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden' }}>
              {[[true, 'Three streams'], [false, 'Together']].map(([v, label]) => (
                <button key={label} onClick={() => { setSplit(v); setAll({}); }} aria-pressed={split === v}
                  style={{ background: split === v ? C.tealDim : 'none', color: split === v ? TEAL : DIM, border: 'none',
                           borderRight: v ? `1px solid ${C.border}` : 'none', padding: '5px 12px', fontSize: 12.5,
                           fontWeight: split === v ? 700 : 500, cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {split ? ['spec', 'plan', 'production'].map(k => {
            const mine = oldest.filter(e => e.stream === k);
            if (!mine.length) return null;
            const w = streamWords(type, k);
            return (
              <Stream key={k} k={k} w={w} list={mine} cap={6} showAll={!!all[k]}
                onShowAll={() => setAll(a => ({ ...a, [k]: true }))} entryProps={entryProps} />
            );
          }) : (
            <Stream k="all" list={oldest} cap={10} showAll={!!all.one}
              onShowAll={() => setAll(a => ({ ...a, one: true }))} entryProps={entryProps} />
          )}
        </>
      )}
    </>
  );
}

// A stream shows its most recent entries; the rest are counted, never dropped.
function Stream({ k, w, list, cap, showAll, onShowAll, entryProps }) {
  const shown = showAll ? list : list.slice(-cap);
  const hidden = list.length - shown.length;
  return (
    <div style={{ marginBottom: 22 }}>
      {w && (
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 9 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: w.colour, flexShrink: 0 }} />
            <span style={{ fontSize: 14.5, fontWeight: 700 }}>{w.title}</span>
            <span style={{ fontSize: 11.5, color: C.muted }}>{list.length} {list.length === 1 ? 'entry' : 'entries'}</span>
            {k === 'production' && (
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: GREEN,
                             border: '1px solid #1d4a3a', background: '#0f2a22', padding: '2px 7px', borderRadius: 4 }}>
                facts — never rewritten
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{w.says}</div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {hidden > 0 && (
          <button onClick={onShowAll}
            style={{ background: 'none', border: `1px dashed ${C.border}`, borderRadius: 7, color: C.muted, padding: '7px 0',
                     fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            {hidden} earlier {hidden === 1 ? 'entry' : 'entries'} — show them
          </button>
        )}
        {shown.map(e => <Entry key={e.key} e={e} {...entryProps} />)}
      </div>
    </div>
  );
}

function OrderVerdict({ v }) {
  const tone = (STAMP[v.stamp] || STAMP.unknown).colour;
  const d = x => (x ? new Date(x + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : null);
  return (
    <div style={{ ...cs.card, marginBottom: 14 }}>
      <div style={{ ...st.eyebrow }}>Did it make its delivery?</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px 14px', flexWrap: 'wrap', fontSize: 13.5 }}>
        <span style={{ color: DIM }}>
          Made {v.started ? <b style={{ color: '#e5e7eb' }}>{d(v.started)}</b> : 'not started'}
          {v.started && (v.ended ? <> – <b style={{ color: '#e5e7eb' }}>{d(v.ended)}</b></> : ' – ongoing')}
          {v.due ? <> · promised <b style={{ color: '#e5e7eb' }}>{d(v.due)}</b></> : ' · no delivery date'}
        </span>
        <Stamp tone={tone} label={v.label} style={{ marginLeft: 'auto' }} />
      </div>
      {(v.closed_from_board > 0 || v.closed_by_floor > 0) && (
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
          {v.closed_by_floor > 0 && `${v.closed_by_floor} run${v.closed_by_floor === 1 ? ' was' : 's were'} ended by the floor, with its own times. `}
          {v.closed_from_board > 0 && `${v.closed_from_board} ${v.closed_by_floor > 0 ? '' : 'run'}${v.closed_from_board === 1 ? ' was' : 's were'} completed from the board, so their days are the board's — kept to what really ran.`}
        </div>
      )}
    </div>
  );
}

// What we promised, over time (the prototype, 24 Sep; built 7 Oct 2026): each
// time the day the order would finish moved, or the delivery it was promised
// for did, how that stood, and what moved it.
const STANDING = { ok: '#e5e7eb', tight: AMBER, late: RED, none: C.muted };
function PromiseOverTime({ p }) {
  const rows = p.rows;
  const day = x => (x ? new Date(String(x).slice(0, 10) + 'T12:00:00')
    .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : null);
  const when = iso => `${new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${timeOf(iso)}`;
  const th = { textAlign: 'left', padding: '7px 14px', fontSize: 10.5, letterSpacing: '0.09em', textTransform: 'uppercase',
               color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '9px 14px', verticalAlign: 'top', borderBottom: `1px solid ${C.border}` };
  const small = { fontSize: 11.5, color: C.muted, marginTop: 3, lineHeight: 1.45 };
  return (
    <div style={{ ...cs.tableWrap, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 14px', background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: DIM }}>What we promised, over time</span>
        {rows.length > 1 && <span style={{ fontSize: 11.5, color: C.muted }}>{rows.length} revisions</span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr><th style={th}>We said, on</th><th style={th}>It would finish</th><th style={th}>Standing</th></tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const tone = r.standing.tone === 'ok' && r.done ? GREEN : STANDING[r.standing.tone] || C.muted;
              return (
                <tr key={i} style={{ background: r.now ? `${TEAL}0d` : 'none' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <b style={{ color: '#e5e7eb' }}>{r.kind === 'finished' ? 'Finished' : when(r.at)}</b>
                    {r.now && <span style={{ color: TEAL, fontWeight: 700 }}> · now</span>}
                    {r.kind === 'taken' && <div style={small}>order taken</div>}
                    {r.kind === 'start' && <div style={small}>as far back as the board's history goes</div>}
                    {r.kind === 'promise' && <div style={{ ...small, color: AMBER }}>delivery moved from {day(r.was_due) || 'none'} to {day(r.due) || 'none'}</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.finish ? <b style={{ color: r.kind === 'finished' ? GREEN : '#e5e7eb' }}>{day(r.finish)}</b>
                      : <span style={{ color: C.mutedDark }}>not on the board</span>}
                    {r.kind === 'board' && r.was && <div style={small}>was {day(r.was)}</div>}
                    {r.kind === 'board' && !r.was && <div style={small}>first on the board</div>}
                  </td>
                  <td style={td}>
                    <span style={{ color: tone, fontWeight: 600 }}>{r.standing.text}</span>
                    {(r.changes || []).map((c, k) => (
                      <div key={k} style={small}>
                        {c.summary || 'A change to the board'}
                        {c.reason && (c.reason.label || c.reason.code) && (
                          <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: DIM, border: `1px solid ${C.border}`,
                                         borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                            {c.reason.label || c.reason.code}
                          </span>
                        )}
                        {c.reason && c.reason.note && <span style={{ color: DIM }}> — {c.reason.note}</span>}
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(p.taken_before_reach || p.finished_unknown) && (
        <div style={{ fontSize: 12, color: C.muted, padding: '9px 14px', lineHeight: 1.5 }}>
          {p.taken_before_reach && p.reach_at && `Before ${when(p.reach_at)} the board kept no pictures of its runs, so what was said earlier can't be shown. `}
          {p.finished_unknown && 'Every run is done, but the day the last one ended is not known.'}
        </div>
      )}
    </div>
  );
}

// A mould's shot limit, refurbishment and service (7 Oct 2026): three bars,
// each from its own last entry in the log, with the board ahead and the pace;
// the count corrected by hand; the log itself.
const fmtN = x => Math.round(Number(x) || 0).toLocaleString('en-GB');
const dayW = d => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
const horizon = m => (m == null ? null : m >= 24 ? `about ${Math.round(m / 12)} years` : m >= 1 ? `about ${m} month${m === 1 ? '' : 's'}` : 'under a month');
const todayLocal = () => new Date().toLocaleDateString('en-CA');
const LOG_KIND = {
  service:       { colour: TEAL,  label: () => 'Service' },
  refurbishment: { colour: AMBER, label: () => 'Refurbishment' },
  renewed:       { colour: GREEN, label: k => (k === 'shot_life' ? 'New mould' : 'New warranty') },
};

function WearBar({ pct, after }) {
  const tone = pct >= 100 ? RED : pct >= 90 ? AMBER : TEAL;
  const fill = { position: 'absolute', left: 0, top: 0, bottom: 0 };
  return (
    <div style={{ position: 'relative', height: 10, background: SUNK, border: `1px solid ${C.border}`, borderRadius: 5, overflow: 'hidden', margin: '6px 0 5px' }}>
      {after > pct && <div style={{ ...fill, width: `${Math.min(100, after)}%`, background: `${tone}38` }} title="With the runs on the board" />}
      <div style={{ ...fill, width: `${Math.min(100, pct)}%`, minWidth: pct > 0 ? 3 : 0, background: tone }} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: '90%', width: 1, background: '#6b7280' }} title="90%" />
    </div>
  );
}

function WearRow({ title, right, pct, after, children }) {
  return (
    <div style={{ padding: '11px 14px', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#e5e7eb' }}>{title}</span>
        {right && <span style={{ fontSize: 12.5, color: DIM, fontVariantNumeric: 'tabular-nums' }}>{right}</span>}
      </div>
      {pct != null && <WearBar pct={pct} after={after ?? pct} />}
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function MouldWear({ w, mouldId, onChanged }) {
  const [mode, setMode] = useState(null);           // null | 'log' | 'count'
  const [showAllLog, setShowAllLog] = useState(false);
  const L = w.limit, R = w.refurbishment, S = w.service;
  const warranty = L.kind !== 'shot_life';
  const name = warranty ? 'warranty' : 'shot life';
  const pace = w.per_month ? ` (${fmtN(w.per_month)} shots a month over the last 90 days)` : '';
  const ahead = (b, what) => {
    if (b.past_limit) return null;
    if (b.reaches_limit) return <div style={{ color: RED }}>The board takes it to the end of its {what} on {dayW(b.reaches_limit)}.</div>;
    if (b.reaches_90 && !b.past_90) return <div style={{ color: AMBER }}>The board takes it past 90% on {dayW(b.reaches_90)}.</div>;
    if (b.months_left != null) return <div>At this pace{pace}: {horizon(b.months_left)} to go.</div>;
    return null;
  };

  // Service: by shots, by time, or both -- the bar shows whichever is nearer.
  let sPct = null, sAfter = null, sRight = null;
  if (S.last) {
    const byShots = S.every ? S.pct : null;
    let byTime = null;
    if (S.due_by_time) {
      const span = (Date.parse(S.due_by_time) - Date.parse(S.last)) / 864e5;
      const gone = (Date.parse(todayLocal()) - Date.parse(S.last)) / 864e5;
      byTime = span > 0 ? Math.round((gone / span) * 1000) / 10 : null;
    }
    sPct = Math.max(byShots ?? -1, byTime ?? -1);
    if (sPct < 0) sPct = null;
    sAfter = byShots != null && byShots === sPct ? S.pct_after_plan : sPct;
    sRight = [S.every ? `${fmtN(S.since)} of ${fmtN(S.every)} shots` : `${fmtN(S.since)} shots`,
              S.due_by_time ? `due by time ${dayW(S.due_by_time)}` : null].filter(Boolean).join(' · ');
  }
  const every = [S.every ? `${fmtN(S.every)} shots` : null, S.every_n ? `${S.every_n} ${S.every_n === 1 ? S.every_unit.replace(/s$/, '') : S.every_unit}` : null].filter(Boolean);

  const shown = showAllLog ? w.log : w.log.slice(0, 5);
  const remove = async (e) => {
    if (!window.confirm(`Take "${LOG_KIND[e.kind].label(L.kind)} on ${dayW(e.day)}" out of the log? It stays in the mould's history.`)) return;
    try { await api.delete(`/api/v1/moulds/${mouldId}/log/${e.id}`); onChanged(); }
    catch (err) { window.alert(err.response?.data?.error || err.message); }
  };

  return (
    <div style={{ ...cs.tableWrap, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: DIM }}>Shot limit, refurbishment and service</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
          <button onClick={() => setMode(mode === 'count' ? null : 'count')} style={{ ...st.textBtn, color: TEAL }}>
            <i className="fa-solid fa-pen" style={{ fontSize: 10, marginRight: 5 }} />Correct the count
          </button>
          <button onClick={() => setMode(mode === 'log' ? null : 'log')} style={{ ...st.textBtn, color: TEAL }}>
            <i className="fa-solid fa-screwdriver-wrench" style={{ fontSize: 10, marginRight: 5 }} />Log a service
          </button>
        </span>
      </div>
      {mode === 'count' && <CountForm mouldId={mouldId} now={L.since} onClose={() => setMode(null)} onDone={() => { setMode(null); onChanged(); }} />}
      {mode === 'log' && <LogForm mouldId={mouldId} kind={L.kind} onClose={() => setMode(null)} onDone={() => { setMode(null); onChanged(); }} />}

      {/* The shot limit */}
      <WearRow title={warranty ? 'Warranty' : 'Shot life'}
        right={L.every ? `${fmtN(L.since)} of ${fmtN(L.every)} shots · ${L.pct}%` : `${fmtN(L.since)} shots`}
        pct={L.every ? L.pct : null} after={L.pct_after_plan}>
        {!L.every && <div>No shot limit set — set one on the mould form.</div>}
        {L.past_limit && <div style={{ color: RED, fontWeight: 600 }}>
          {warranty ? 'Out of warranty — agree with the customer what comes next.' : 'Past its shot life — the customer needs a new mould.'}</div>}
        {L.base === 'counted' && <div>Counted by hand: {fmtN(L.entered)} by {dayW(L.base_from)}; {fmtN(L.made)} since.</div>}
        {L.base === 'renewed' && <div>{warranty ? 'New warranty' : 'New mould'} on {dayW(L.renewed_on)}; {fmtN(L.made)} since.</div>}
        {L.base === 'estimate' && <div>{fmtN(L.entered)} estimated before {dayW(w.app_from)}; {fmtN(L.made)} counted since.</div>}
        {L.base === 'unknown' && <div style={{ color: AMBER }}>{fmtN(L.made)} counted since {dayW(w.app_from)}. Shots before then are not known — correct the count when you know it.</div>}
        {w.planned > 0 && L.every && <div>The runs on the board add {fmtN(w.planned)} more ({L.pct_after_plan}%).</div>}
        {L.every && ahead(L, name)}
      </WearRow>

      {/* Refurbishment */}
      <WearRow title="Refurbishment"
        right={R.last ? (R.every ? `${fmtN(R.since)} of ${fmtN(R.every)} shots · ${R.pct}%` : `${fmtN(R.since)} shots since`) : null}
        pct={R.last && R.every ? R.pct : null} after={R.pct_after_plan}>
        {!R.every && !R.last && <div>No interval set, and none logged.</div>}
        {R.every && !R.last && <div>Every {fmtN(R.every)} shots. None logged yet — log the last one to start counting.</div>}
        {R.last && <div>Last on {dayW(R.last)}.{R.since_known === false && ' The shots between it and when the app began counting are not known.'}{!R.every && ' No interval set.'}</div>}
        {R.past_limit && <div style={{ color: RED, fontWeight: 600 }}>Due now: {fmtN(R.since)} shots since the last one.</div>}
        {R.last && R.every && ahead(R, 'refurbishment interval')}
      </WearRow>

      {/* Service */}
      <WearRow title="Service" right={sRight} pct={sPct} after={sAfter}>
        {!every.length && !S.last && <div>No interval set, and none logged.</div>}
        {every.length > 0 && <div>Every {every.join(' or ')}{every.length > 1 ? ', whichever comes first' : ''}.</div>}
        {every.length > 0 && !S.last && <div>None logged yet — log the last one to start counting.</div>}
        {S.last && <div>Last on {dayW(S.last)}.{S.since_known === false && ' The shots between it and when the app began counting are not known.'}</div>}
        {S.overdue && <div style={{ color: RED, fontWeight: 600 }}>Overdue{S.due ? ` since ${dayW(S.due)}` : ''}.</div>}
        {S.last && !S.overdue && S.due && <div style={{ color: S.due === S.reaches_limit ? AMBER : C.muted }}>Next due {dayW(S.due)}{S.due === S.reaches_limit ? ', as the board stands' : ''}.</div>}
      </WearRow>

      {/* The log */}
      <div style={{ padding: '10px 14px' }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>
          Log · {w.log.length}
        </div>
        {w.log.length === 0 && <div style={{ fontSize: 12.5, color: C.muted }}>Nothing logged yet.</div>}
        {shown.map(e => (
          <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0', borderTop: `1px solid ${C.border}`, fontSize: 12.5 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: LOG_KIND[e.kind].colour, border: `1px solid ${LOG_KIND[e.kind].colour}55`,
                           borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{LOG_KIND[e.kind].label(L.kind)}</span>
            <span style={{ color: '#e5e7eb', whiteSpace: 'nowrap' }}>{dayW(e.day)}</span>
            <span style={{ color: DIM, flex: 1 }}>{e.task}{e.notes && <span style={{ color: C.muted }}> — {e.notes}</span>}</span>
            <button onClick={() => remove(e)} title="Take out of the log (kept in the history)" aria-label="Take out of the log"
              style={{ ...st.textBtn, color: C.muted }}>×</button>
          </div>
        ))}
        {w.log.length > 5 && !showAllLog && (
          <button onClick={() => setShowAllLog(true)} style={{ ...st.textBtn, color: TEAL, marginTop: 4 }}>Show all {w.log.length}</button>
        )}
      </div>
    </div>
  );
}

const formRow = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' };
const formLabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.04em' };
const formInput = { background: SUNK, border: `1px solid ${C.border}`, borderRadius: 6, color: '#e5e7eb', padding: '6px 8px', fontSize: 13 };

function FormShell({ title, says, error, saving, onSave, onClose, children }) {
  return (
    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, background: `${TEAL}0a` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#e5e7eb' }}>{title}</div>
      {says && <div style={{ fontSize: 12, color: C.muted, margin: '3px 0 9px', lineHeight: 1.5 }}>{says}</div>}
      <div style={formRow}>
        {children}
        <span style={{ display: 'flex', gap: 8 }}>
          <button onClick={onSave} disabled={saving}
            style={{ background: TEAL, color: '#0b1215', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} style={{ ...st.textBtn, color: DIM }}>Cancel</button>
        </span>
      </div>
      {error && <div style={{ fontSize: 12.5, color: AMBER, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function CountForm({ mouldId, now, onClose, onDone }) {
  const [shots, setShots] = useState(String(now ?? ''));
  const [day, setDay] = useState(todayLocal());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setError(null);
    try { await api.post(`/api/v1/moulds/${mouldId}/count`, { shots, day }); onDone(); }
    catch (err) { setError(err.response?.data?.error || err.message); setSaving(false); }
  };
  return (
    <FormShell title="Correct the shot count" error={error} saving={saving} onSave={save} onClose={onClose}
      says={`What the tool had really done by the end of a day — the app counts on from there. It counts ${fmtN(now)} now. The correction, and what the app had counted, are kept in the mould's history.`}>
      <label style={formLabel}>SHOTS<input type="number" min="0" value={shots} onChange={e => setShots(e.target.value)} style={{ ...formInput, width: 140 }} /></label>
      <label style={formLabel}>AS OF<input type="date" max={todayLocal()} value={day} onChange={e => setDay(e.target.value)} style={formInput} /></label>
    </FormShell>
  );
}

export function LogForm({ mouldId, kind: limitKind, onClose, onDone }) {
  const [kind, setKind] = useState('service');
  const [day, setDay] = useState(todayLocal());
  const [task, setTask] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setError(null);
    try { await api.post(`/api/v1/moulds/${mouldId}/log`, { kind, day, task, notes }); onDone(); }
    catch (err) { setError(err.response?.data?.error || err.message); setSaving(false); }
  };
  const says = kind === 'renewed'
    ? (limitKind === 'shot_life' ? 'A new mould: the shot life, refurbishment and service all count again from this day.'
                                 : 'A new warranty: the warranty counts again from this day. Refurbishment and service carry on as they were.')
    : kind === 'refurbishment' ? 'The big service: refurbishment and service count again from this day.'
    : 'Service counts again from this day.';
  return (
    <FormShell title="Log a service" says={says} error={error} saving={saving} onSave={save} onClose={onClose}>
      <label style={formLabel}>WHAT
        <select value={kind} onChange={e => setKind(e.target.value)} style={formInput}>
          <option value="service">Service</option>
          <option value="refurbishment">Refurbishment</option>
          <option value="renewed">{limitKind === 'shot_life' ? 'New mould' : 'New warranty'}</option>
        </select>
      </label>
      <label style={formLabel}>DONE ON<input type="date" max={todayLocal()} value={day} onChange={e => setDay(e.target.value)} style={formInput} /></label>
      <label style={{ ...formLabel, flex: '1 1 220px' }}>WHAT WAS DONE
        <input value={task} onChange={e => setTask(e.target.value)} placeholder="e.g. taken apart, cleaned, ejector pins greased" style={formInput} />
      </label>
      <label style={{ ...formLabel, flex: '1 1 180px' }}>NOTES
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="optional — who did it, parts replaced…" style={formInput} />
      </label>
    </FormShell>
  );
}

function Stamp({ tone, label, style }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                   color: tone, background: `${tone}16`, border: `1px solid ${tone}44`, borderRadius: 5, padding: '3px 9px', ...style }}>
      {label}
    </span>
  );
}

function RunsTable({ type, runs, names, onFocus }) {
  const [showAll, setShowAll] = useState(false);
  const t = TABLE[type];
  const shown = showAll ? runs : runs.slice(0, 10);
  const nm = (k, i) => (i == null ? null : (names[k] || {})[i] || `#${i}`);
  const link = (k, i, label) => (i == null ? <span style={{ color: C.mutedDark }}>—</span> : (
    <button onClick={() => onFocus(k, i)} style={{ ...st.textBtn, color: '#e5e7eb', fontWeight: 600, fontSize: 13, textAlign: 'left' }}>{label}</button>
  ));
  const cell = (r, c) => {
    if (c === 'order') return r.order ? link('order', r.order.id, r.order.number) : <span style={{ color: C.mutedDark }}>no order</span>;
    if (c === 'part') return link('part', r.part_id, nm('part', r.part_id));
    if (c === 'machine') return link('machine', r.machine_id, nm('machine', r.machine_id));
    if (c === 'qty') return <><b style={{ color: '#e5e7eb' }}>{r.qty.toLocaleString('en-GB')}</b> pcs</>;
    if (c === 'planned') {
      const now = span(r.planned_from, r.planned_to), first = span(r.first_from, r.first_to);
      return <>{now}{first && first !== now && <div style={{ fontSize: 11.5, color: AMBER }}>first {first}</div>}</>;
    }
    if (c === 'ran') return r.ran_from ? (span(r.ran_from, r.ran_to) || `from ${span(r.ran_from)}`) : <span style={{ color: C.mutedDark }}>{r.state === 'running' ? 'running' : 'not yet'}</span>;
    if (c === 'made') return r.made != null ? <b style={{ color: '#e5e7eb' }}>{r.made.toLocaleString('en-GB')}</b> : <span style={{ color: C.mutedDark }}>—</span>;
    if (c === 'outcome') return <Stamp tone={(STAMP[r.verdict.stamp] || STAMP.unknown).colour} label={r.verdict.label} />;
    return null;
  };
  return (
    <div style={{ ...cs.tableWrap, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 14px', background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: DIM }}>{t.title}</span>
        <span style={{ fontSize: 11.5, color: C.muted }}>{runs.length} run{runs.length === 1 ? '' : 's'}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>{t.cols.map(c => (
              <th key={c} style={{ textAlign: 'left', padding: '7px 14px', fontSize: 10.5, letterSpacing: '0.09em', textTransform: 'uppercase', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>{COL_HEAD[c]}</th>
            ))}</tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.key}>
                {t.cols.map(c => (
                  <td key={c} style={{ padding: '7px 14px', borderBottom: `1px solid ${C.border}`, color: DIM, fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' }}>{cell(r, c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {runs.length > shown.length && (
        <button onClick={() => setShowAll(true)} style={{ ...st.textBtn, padding: '9px 14px' }}>
          {runs.length - shown.length} earlier run{runs.length - shown.length === 1 ? '' : 's'} — show them
        </button>
      )}
    </div>
  );
}

// Everything this one touches, worked out from its own entries and runs, so it
// needs no new data and cannot drift out of date. What makes a trail
// followable rather than merely readable (the prototype, 24 Sep).
function Touches({ type, id, entries, runs, names, onFocus }) {
  const rows = ['order', 'part', 'mould', 'machine'].filter(k => k !== type).map(k => {
    const ids = [];
    for (const e of entries) { const v = e.links && e.links[k]; if (v != null && !ids.includes(v)) ids.push(v); }
    for (const r of runs) {
      const v = k === 'order' ? (r.order && r.order.id) : r[k + '_id'];
      if (v != null && !ids.includes(v)) ids.push(v);
    }
    return [k, ids.filter(i => (names[k] || {})[i])];
  }).filter(([, ids]) => ids.length);
  if (!rows.length) return null;
  return (
    <div style={{ background: SUNK, border: `1px solid ${C.border}`, borderRadius: 9, padding: '11px 14px', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
      <div style={{ fontSize: 11.5, color: C.muted }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEAL, marginRight: 8 }}>Touches</span>
        every chip opens that thing's own history
      </div>
      {rows.map(([k, ids]) => (
        <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, minWidth: 72 }}>{TYPES[k].label}</span>
          {ids.slice(0, 6).map(i => (
            <button key={i} onClick={() => onFocus(k, i)} style={st.linkChip}>{names[k][i]}</button>
          ))}
          {ids.length > 6 && <span style={{ fontSize: 11.5, color: C.muted }}>+{ids.length - 6} more</span>}
        </div>
      ))}
    </div>
  );
}

// ── A reason, added afterwards ──────────────────────────────────────────────
//
// What a diff cannot show: why (the director, 1 Oct 2026 -- "changed it too
// quickly, before the process was stable"). The reason is the one part of an
// entry that may change; everything else stays as it was recorded.

function ReasonEditor({ e, reason, reasons, onClose, onSaved }) {
  const [code, setCode] = useState((reason && reason.code) || '');
  const [note, setNote] = useState((reason && reason.note) || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const groups = useMemo(() => {
    const by = new Map();
    for (const r of reasons.filter(x => x.active || x.code === code)) {
      const g = r.reason_group || 'Other';
      if (!by.has(g)) by.set(g, []);
      by.get(g).push(r);
    }
    return [...by.entries()];
  }, [reasons, code]);

  async function save() {
    setBusy(true); setError(null);
    const body = { reason_code: code || null, reason_note: note.trim() || null };
    try {
      if (e.source === 'record') await api.put(`/api/v1/history/records/${e.id}/reason`, body);
      else await api.put(`/api/v1/reasons/event/${e.id}`, body);
      onSaved(code || note.trim() ? { code: code || null, note: note.trim() || null } : null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 9, padding: '12px 14px', background: `${TEAL}0d`, border: `1px solid ${TEAL}44`, borderRadius: 9 }}>
      <div style={{ fontSize: 12.5, color: DIM, marginBottom: 8, lineHeight: 1.5 }}>
        Why was this changed? The reason is kept with the entry; nothing else in it changes.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select value={code} onChange={ev => setCode(ev.target.value)}
          style={{ ...cs.input, width: 'auto', minWidth: 200, flex: '0 1 240px', background: SUNK }}>
          <option value="">No reason from the list</option>
          {groups.map(([g, list]) => (
            <optgroup key={g} label={g}>
              {list.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </optgroup>
          ))}
        </select>
        <input value={note} onChange={ev => setNote(ev.target.value)} maxLength={500}
          placeholder="In your own words — e.g. changed too soon, the process was not stable yet"
          onKeyDown={ev => { if (ev.key === 'Enter') save(); if (ev.key === 'Escape') onClose(); }}
          style={{ ...cs.input, flex: '1 1 280px', background: SUNK }} />
      </div>
      {error && <div style={{ fontSize: 12.5, color: AMBER, marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={save} disabled={busy}
          style={{ padding: '7px 14px', background: TEAL, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700,
                   fontSize: 12.5, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Saving…' : 'Save the reason'}
        </button>
        <button onClick={onClose} style={st.ghost}>Cancel</button>
      </div>
    </div>
  );
}

// ── Revert to this ──────────────────────────────────────────────────────────
//
// Lands this one record on this version and touches nothing else (the
// director, 24 Sep). The preview is worked out on the server from the record
// as it is now; the revert is refused if it changed in between, and is saved
// as a new version, so it can be reverted too.

const REVERTIBLE = new Set(['part', 'machine', 'mould', 'material', 'product']);

function RevertPanel({ e, onClose, onDone }) {
  const [plan, setPlan]   = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);

  const load = useCallback(() => {
    setPlan(null); setError(null);
    api.get(`/api/v1/history/revert/${e.id}`)
      .then(r => setPlan(r.data))
      .catch(err => setError(err.response?.data || { error: err.message }));
  }, [e.id]);
  useEffect(() => { load(); }, [load]);

  async function apply() {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/v1/history/revert/${e.id}`, { expect_latest: plan.latest });
      onDone();
    } catch (err) {
      const d = err.response?.data || { error: err.message };
      setError(d);
      if (d.fields) setPlan(d);
    } finally {
      setBusy(false);
    }
  }

  const n = plan ? plan.fields.length : 0;
  return (
    <div style={{ marginTop: 9, padding: '12px 14px', background: `${AMBER}0d`, border: `1px solid ${AMBER}44`, borderRadius: 9 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24', marginBottom: 6 }}>
        Revert {e.name} to version {e.version}?
      </div>
      {error && (
        <div style={{ fontSize: 12.5, color: DIM, marginBottom: 8, lineHeight: 1.55 }}>
          <div style={{ color: '#e5e7eb' }}>{error.error}</div>
          {error.why && <div>{error.why}</div>}
          {(error.options || []).map(o => <div key={o.label}>· {o.label}: {o.why}</div>)}
        </div>
      )}
      {!plan && !error && <div style={{ fontSize: 12.5, color: C.muted }}>Working out what would change…</div>}
      {plan && (n === 0 ? (
        <div style={{ fontSize: 12.5, color: DIM }}>
          Nothing to change: {plan.record.name} is already as it was at version {plan.version}.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: DIM, marginBottom: 4, lineHeight: 1.55 }}>
            {n === 1 ? 'This field goes' : `These ${n} fields go`} back to what {n === 1 ? 'it was' : 'they were'} at
            version {plan.version}. Nothing else changes, and the revert is saved as a new version, so it can be reverted too.
          </div>
          <ChangeTable fields={plan.fields.map(f => f.field)}
            before={Object.fromEntries(plan.fields.map(f => [f.field, f.now]))}
            after={Object.fromEntries(plan.fields.map(f => [f.field, f.becomes]))}
            heads={['Field', 'Now', 'Becomes']} />
        </>
      ))}
      {plan && plan.notes.map(t => (
        <div key={t} style={{ fontSize: 12, color: DIM, marginTop: 7, lineHeight: 1.5 }}>
          <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />{t}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {plan && n > 0 && (
          <button onClick={apply} disabled={busy}
            style={{ padding: '7px 14px', background: AMBER, color: '#111', border: 'none', borderRadius: 8,
                     fontWeight: 700, fontSize: 12.5, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Reverting…' : `Revert to version ${plan.version}`}
          </button>
        )}
        {error && (error.options || []).some(o => o.do === 'reload') && <button onClick={load} style={st.ghost}>Look again</button>}
        <button onClick={onClose} style={st.ghost}>Cancel</button>
      </div>
    </div>
  );
}

// ── No longer in use ────────────────────────────────────────────────────────

function NoLongerInUse({ onFocus, navigate }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/api/v1/history/retired')
      .then(({ data }) => setItems(data.items))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const by = {};
    for (const i of items) (by[i.type] = by[i.type] || []).push(i);
    return Object.keys(TYPES).filter(k => by[k]).map(k => [k, by[k]]);
  }, [items]);

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>;
  if (error) return <Notice tone={AMBER}>{error}</Notice>;
  if (!items.length) return <Empty title="Everything is in use" body="Parts and products discontinued, equipment decommissioned, orders fulfilled and production records archived appear here." />;

  return (
    <>
      <p style={{ color: DIM, fontSize: 13, lineHeight: 1.6, margin: '0 0 18px', maxWidth: 680 }}>
        Read from History itself: each thing's latest word was that it went out of use. Bring one back
        from its own page — the link beside each group — and it leaves this list.
      </p>
      {groups.map(([k, list]) => {
        const t = TYPES[k];
        const home = RETIRED_HOME[k] ? RETIRED_HOME[k]() : t.open && t.open();
        return (
          <div key={k} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
              <div style={{ ...st.eyebrow, marginBottom: 0 }}>
                <i className={`fa-solid ${t.icon}`} style={{ marginRight: 6 }} />{t.label} · {list.length}
              </div>
              {home && <button onClick={() => navigate(home)} style={st.textBtn}>Open {t.label.toLowerCase()} <i className="fa-solid fa-arrow-right" style={{ fontSize: 9, marginLeft: 3 }} /></button>}
            </div>
            <div style={{ ...cs.tableWrap }}>
              {list.map((i, n) => (
                <div key={i.id} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px',
                                         borderTop: n ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, overflowWrap: 'anywhere' }}>{i.name || `${t.one} ${i.id}`}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {i.date_known ? `Since ${longDate(i.retired_on)}` : 'Date it happened was not kept'}
                      {i.by ? ` · ${i.by}` : ''}
                    </div>
                  </div>
                  <button onClick={() => onFocus(k, i.id)} style={st.ghost}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 11, marginRight: 5 }} />History
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ── small pieces ────────────────────────────────────────────────────────────

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{ padding: '5px 12px', fontSize: 12.5, borderRadius: 7, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
               background: active ? `${TEAL}20` : 'transparent', border: `1px solid ${active ? TEAL : C.border}`,
               color: active ? TEAL : DIM, fontWeight: active ? 700 : 500 }}>
      {children}
    </button>
  );
}

function Notice({ tone, children }) {
  return (
    <div style={{ marginBottom: 14, padding: '11px 13px', background: `${tone}12`, border: `1px solid ${tone}44`,
                  borderRadius: 9, fontSize: 13, color: DIM }}>{children}</div>
  );
}

function Empty({ title, body }) {
  return (
    <div style={{ padding: '26px 20px', background: SUNK, border: `1px dashed ${C.border}`, borderRadius: 11, textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: DIM }}>{title}</div>
      <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.6, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>{body}</div>
    </div>
  );
}

const st = {
  crumbBtn: { background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 },
  eyebrow:  { fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 9 },
  ghost:    { padding: '7px 13px', background: 'transparent', color: DIM, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, whiteSpace: 'nowrap' },
  textBtn:  { background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: 12, padding: 0 },
  nameLink: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: TEAL, fontSize: 12.5, fontWeight: 700, marginBottom: 2, textAlign: 'left' },
  linkChip: { background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 6, color: DIM, fontSize: 11.5, padding: '2px 8px', cursor: 'pointer' },
};
